import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  MovementCalculator,
  PositionCalculator,
  SelectionCallback,
} from 'features/assetViz/cameras/DefaultCamera'
import { PipeEccentricReducerSurface3D } from 'features/assetViz/objects/surfaces/PipeEccentricReducerSurface3D'
import { PipeElbowSurface3D } from 'features/assetViz/objects/surfaces/PipeElbowSurface3D'
import { PipeSurface3D } from 'features/assetViz/objects/surfaces/PipeSurface3D'
import { PipeTJointSurface3D } from 'features/assetViz/objects/surfaces/PipeTJointSurface3D'
import { SceneFactoryConfig } from 'features/assetViz/scenes/sceneFactory'
import { CameraBounds } from 'features/assetViz/utils/helpers'
import React from 'react'
import {
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoMeasurementUnit,
  PipeOrientation,
} from 'types'

export const PipingSceneConfig: SceneFactoryConfig = {
  name: 'Piping',
  assetModels: [GeckoAssetModelType.Piping],
  componentModels: [
    GeckoComponentModelType.PipeSection,
    GeckoComponentModelType.PipeElbow,
    GeckoComponentModelType.PipeTJoint,
    GeckoComponentModelType.PipeEccentricReducer,
  ],
  sceneFunc: (props) => <PipingScene {...props} />,
}

/**
 * A scene depicting a cylindrical pipe (not a smoking pipe, mind you) which
 * wraps around the x-axis.
 * @param assetModel      The model used to represent the whole asset
 * @param componentModel  The model for the component in question
 */
export const PipingScene: React.FC<{
  assetModel: GeckoAssetModel
  componentModel: GeckoComponentModel
}> = ({ componentModel: selectedModel }) => {
  //----------------------------------------------------------------------------
  const { show3D, componentDataFrames } = useAssetVizInternal()

  const componentBounds = React.useMemo(() => {
    const componentBounds =
      selectedModel.componentBounds ??
      GeckoComponentRect.CreateFromXYs(
        '',
        0,
        0,
        1,
        1,
        GeckoMeasurementUnit.Meters,
        GeckoMeasurementUnit.Meters
      )
    if (
      componentBounds.x.unit !== GeckoMeasurementUnit.Meters ||
      componentBounds.y.unit !== GeckoMeasurementUnit.Meters
    )
      throw new Error(
        `Pipes in the asset model should always be in meters, but 
              the x range is in ${componentBounds.x.unit}
              and y range is in ${componentBounds.y.unit}`
      )
    return componentBounds
  }, [selectedModel.componentBounds])

  const radius = React.useMemo(() => {
    if (selectedModel.type === GeckoComponentModelType.PipeSection) {
      return selectedModel.radius
    } else if (selectedModel.type === GeckoComponentModelType.PipeElbow) {
      return selectedModel.majorRadius
    } else if (selectedModel.type === GeckoComponentModelType.PipeTJoint) {
      // For a T-Joint rotate about the chord
      return selectedModel.chord_length
    } else if (
      selectedModel.type === GeckoComponentModelType.PipeEccentricReducer
    ) {
      return selectedModel.inlet_radius
    }

    throw new Error(
      `Piping scene can only be used with pipe sections, elbows, t-joints, or eccentric reducers, but got ${selectedModel.id}`
    )
  }, [selectedModel])

  //----------------------------------------------------------------------------

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      componentDataFrames
        ?.at(0)
        ?.data?.geometry?.project(componentBounds.space) ?? componentBounds,
    [componentBounds, componentDataFrames]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(() => {
    const bounds: CameraBounds = {
      rect: componentBounds,
      doWrapX: show3D,
    }

    if (!show3D && selectedModel.type === GeckoComponentModelType.PipeTJoint) {
      bounds.distToComponentMax = 1.6
      bounds.distToComponentMin = 1.5
    }

    if (
      show3D &&
      selectedModel.type === GeckoComponentModelType.PipeEccentricReducer
    ) {
      bounds.doWrapX = false
      bounds.doWrapY = true
    }

    return bounds
  }, [componentBounds, selectedModel, show3D])

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      const relX = componentX - (componentBounds.start.x.num ?? 0)
      const relY = componentY - (componentBounds.start.y.num ?? 0)

      if (!show3D || !selectedModel) {
        if (
          selectedModel &&
          selectedModel.type === GeckoComponentModelType.PipeTJoint
        ) {
          return {
            position: [relX, relY, distToComponent],
            rotation: [0, 0, 0],
          }
        }

        return {
          position: [relX, relY, distToComponent],
          rotation:
            selectedModel.type === GeckoComponentModelType.PipeSection &&
            selectedModel.orientation === PipeOrientation.Horizontal
              ? [0, 0, 0.5 * Math.PI]
              : [0, 0, 0],
        }
      }

      const angle = 2.0 * Math.PI * (relX / componentBounds.width.num - 0.25)
      const r = distToComponent + radius.num

      if (selectedModel.type === GeckoComponentModelType.PipeElbow) {
        const rotationCenterX = selectedModel.majorRadius.num
        return {
          position: [
            rotationCenterX + r * Math.sin(angle),
            selectedModel.angle < 270
              ? relY + relY * Math.cos((selectedModel.angle * Math.PI) / 180)
              : relY + relY * Math.sin((selectedModel.angle * Math.PI) / 180),
            r * Math.cos(angle),
          ],
          rotation: [0, angle, 0],
        }
      }

      if (selectedModel.type === GeckoComponentModelType.PipeTJoint) {
        return {
          position: [r * Math.sin(angle), r * Math.cos(angle), relY],
          rotation: [(Math.PI * 3) / 2, angle, Math.PI],
        }
      }

      if (selectedModel.type === GeckoComponentModelType.PipeEccentricReducer) {
        const angle =
          2 * Math.PI * (relY / componentBounds.height.num) + Math.PI / 2

        return {
          position: [
            relX,
            distToComponent * Math.sin(angle),
            selectedModel.inlet_radius.num + distToComponent * Math.cos(angle),
          ],
          rotation: [Math.PI - angle, Math.PI, Math.PI],
        }
      }

      return {
        position: [r * Math.sin(angle), relY, r * Math.cos(angle)],
        rotation: [
          0,
          angle,
          selectedModel.type === GeckoComponentModelType.PipeSection &&
          selectedModel.orientation === PipeOrientation.Horizontal
            ? 0.5 * Math.PI
            : 0,
        ],
      }
    },
    [show3D, componentBounds, radius, selectedModel]
  )

  const getMovementForCursorDelta = React.useCallback<MovementCalculator>(
    ([x, y]) => {
      if (
        selectedModel.type === GeckoComponentModelType.PipeTJoint ||
        (selectedModel.type === GeckoComponentModelType.PipeSection &&
          selectedModel.orientation === PipeOrientation.Vertical)
      ) {
        return [x, y]
      }

      if (
        (selectedModel.type === GeckoComponentModelType.PipeElbow ||
          selectedModel.type ===
            GeckoComponentModelType.PipeEccentricReducer) &&
        show3D
      ) {
        return [x, y]
      }

      return [y, -x]
    },
    [selectedModel, show3D]
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) => {
      if (show3D) {
        return [Math.min(rect.width.num, radius.num * 2.0), rect.height.num]
      }
      return [rect.width.num, rect.height.num]
    },
    [radius, show3D]
  )

  //----------------------------------------------------------------------------

  return (
    <>
      <DefaultCamera
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getMovementForCursorDelta={getMovementForCursorDelta}
        getCameraDimensionsFit={getCameraDimensionsFit}
      />
      {/* Pipe shell ------------------------------------------------------- */}
      {selectedModel.type === GeckoComponentModelType.PipeSection ? (
        <PipeSurface3D model={selectedModel} />
      ) : selectedModel.type === GeckoComponentModelType.PipeElbow ? (
        <PipeElbowSurface3D model={selectedModel} />
      ) : selectedModel.type === GeckoComponentModelType.PipeTJoint ? (
        <PipeTJointSurface3D model={selectedModel} />
      ) : selectedModel.type ===
        GeckoComponentModelType.PipeEccentricReducer ? (
        <PipeEccentricReducerSurface3D model={selectedModel} />
      ) : null}
    </>
  )
}
